import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, FileText, Briefcase } from "lucide-react";
import { quoteJobService } from "../../../services/quoteJobService";
import { clientService } from "../../../services/clientService";
import { toast } from "react-hot-toast";

import Stepper from "./quote-job/Stepper";
import ClientSelection from "./quote-job/ClientSelection";
import QuoteForm from "./quote-job/QuoteForm";
import JobForm from "./quote-job/JobForm";
import NavigationButtons from "./quote-job/NavigationButtons";

export default function CreateQuoteJob() {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);

    const [selectedClient, setSelectedClient] = useState(null);
    const [clientMode, setClientMode] = useState("search");
    const [newClientData, setNewClientData] = useState({
        title: "",
        first_name: "",
        last_name: "",
        company_name: "",
        email: "",
        password: "",
        phone: "",
        street_1: "",
        city: "",
        state: "",
        postal_code: "",
        country: "USA"
    });

    const [quoteData, setQuoteData] = useState({
        boat_type: "",
        boat_year: "",
        boat_make: "",
        boat_length: "",
        boat_location: "",
        zip_code: "",
        message: "",
        is_boat_in_water: true,
        dock: "",
        slip_number: "",
        service_id: "",
        lead_source_id: "",
        amount: "",
        payment_method: "",
        fees: [],
        quote_fuel: "",
        offer_description: "",
        agreement_type: "",
        report_generation_fees: "",
        estimated_days: ""
    });

    const [jobData, setJobData] = useState({
        start_date: "",
        start_time: "",
        end_time: "",
        location: "",
        is_boat_in_water: true,
        visit_instructions: "",
        notes: "",
        surveyors: []
    });

    const [services, setServices] = useState([]);
    const [leadSources, setLeadSources] = useState([]);
    const [surveyors, setSurveyors] = useState([]);

    const steps = [
        { number: 1, title: "Client", icon: User, description: "Select or create client" },
        { number: 2, title: "Quote", icon: FileText, description: "Quote details" },
        { number: 3, title: "Job", icon: Briefcase, description: "Job assignment" }
    ];

    useEffect(() => {
        loadServices();
        loadLeadSources();
        loadSurveyors();
    }, []);

    const loadServices = async () => {
        try {
            const response = await quoteJobService.getServices();
            if (response.data.success) {
                setServices(response.data.data || []);
            }
        } catch (error) {
            console.log("Services not available");
        }
    };

    const loadLeadSources = async () => {
        try {
            const response = await quoteJobService.getLeadSources();
            if (response.data.success) {
                setLeadSources(response.data.data || []);
            }
        } catch (error) {
            console.log("Lead sources not available");
        }
    };

    const loadSurveyors = async () => {
        try {
            const response = await quoteJobService.getSurveyors();
            if (response.data.success) {
                setSurveyors(response.data.data || []);
            }
        } catch (error) {
            console.log("Surveyors not available");
        }
    };

    const validateStep1 = () => {
        if (!selectedClient && !newClientData.first_name) {
            toast.error("Please select a client or fill in new client details");
            return false;
        }

        if (!selectedClient) {
            if (!newClientData.first_name || !newClientData.last_name || !newClientData.email) {
                toast.error("Please fill in required client fields (First Name, Last Name, Email)");
                return false;
            }
        }

        return true;
    };

    const validateStep2 = () => {
        if (!quoteData.boat_type || !quoteData.boat_make) {
            toast.error("Please fill in required quote fields (Boat Type, Boat Make)");
            return false;
        }

        if (quoteData.payment_method === "stripe" || quoteData.payment_method === "cash") {
            if (!quoteData.offer_description) {
                toast.error("Please fill in the Note (Offer Description)");
                return false;
            }
            if (!quoteData.agreement_type) {
                toast.error("Please select an Agreement Type");
                return false;
            }
        }
        return true;
    };

    const handleNext = async () => {
        if (currentStep === 1) {
            if (!validateStep1()) return;

            // If in "create" mode and client already exists (was created previously), update it
            if (clientMode === "create" && selectedClient) {
                setLoading(true);
                try {
                    const response = await clientService.updateClient(selectedClient.id, newClientData);
                    if (response.data.success) {
                        toast.success("Client updated successfully");
                        setSelectedClient(response.data.data);
                    } else {
                        throw new Error("Failed to update client");
                    }
                } catch (error) {
                    toast.error(error.response?.data?.message || "Failed to update client");
                    return; // Stop here if update fails
                } finally {
                    setLoading(false);
                }
            } else if (clientMode === "create" && !selectedClient) {
                // If in "create" mode and no client exists yet, create new client
                setLoading(true);
                try {
                    const response = await clientService.createClient(newClientData);
                    if (response.data.success) {
                        toast.success("Client created successfully");
                        setSelectedClient(response.data.data);
                    } else {
                        throw new Error("Failed to create client");
                    }
                } catch (error) {
                    toast.error(error.response?.data?.message || "Failed to create client");
                    return; // Stop here if creation fails
                } finally {
                    setLoading(false);
                }
            }
            // If in "search" mode and client is selected, no need to create or update
        }

        if (currentStep === 2 && !validateStep2()) return;

        setCurrentStep(prev => Math.min(prev + 1, 3));
    };

    const handlePrevious = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // Check if we have a client (should be guaranteed by step 1 logic now)
            if (!selectedClient) {
                toast.error("Client information is missing");
                return;
            }

            // Prepare quote data with payment information
            const quotePayload = { ...quoteData };

            // Convert amount to number if it exists
            if (quotePayload.amount) {
                quotePayload.amount = parseFloat(quotePayload.amount);
            }

            // Handle offer description and agreement type for both payment methods
            if (quoteData.offer_description) {
                quotePayload.message = quoteData.offer_description;
            }
            if (quoteData.agreement_type) {
                quotePayload.agreement_type = quoteData.agreement_type;
            }
            // Remove the temporary key
            delete quotePayload.offer_description;

            // Specific payment method logic
            if (quoteData.payment_method === "stripe") {
                quotePayload.paid_method = "stripe";
                // Convert fees values to numbers
                quotePayload.fees = (quoteData.fees || []).map(fee => ({
                    value: typeof fee.value === 'string' ? parseFloat(fee.value) : fee.value
                }));
                // Remove quote_fuel for stripe
                delete quotePayload.quote_fuel;
            } else if (quoteData.payment_method === "cash") {
                quotePayload.paid_method = "cash";
                quotePayload.fees = [];
                // Include quote_fuel as decimal for cash
                quotePayload.quote_fuel = quoteData.quote_fuel ? parseFloat(quoteData.quote_fuel) : null;
            }

            // Common field for report generation fees
            if (quoteData.report_generation_fees) {
                quotePayload.report_generation_fees = parseFloat(quoteData.report_generation_fees);
            }

            // estimated_days common for both
            if (quoteData.estimated_days) {
                quotePayload.estimated_days = parseInt(quoteData.estimated_days);
            }

            const payload = {
                quote: quotePayload,
                job: jobData,
                client_id: selectedClient.id // Always use the ID now
            };

            const response = await quoteJobService.createQuoteJob(payload);

            if (response.data.success) {
                toast.success("Quote and Job created successfully!");
                navigate("/profile/jobs");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to create quote and job");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="min-h-screen text-sm bg-gray-50">
            <div className="px-4 py-6 bg-white border-b border-gray-200 shadow-sm">
                <div>
                    <h1 className="text-xl font-bold text-gray-900">Create Quote & Job</h1>
                    <p className="text-xs text-gray-600 mt-1">
                        Complete all steps to create a new quote and job assignment
                    </p>
                </div>
            </div>

            <div className="py-6">
                <div className="px-4">
                    <Stepper steps={steps} currentStep={currentStep} />

                    <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                        <div className="p-6 md:p-8">
                            {currentStep === 1 && (
                                <ClientSelection
                                    selectedClient={selectedClient}
                                    setSelectedClient={setSelectedClient}
                                    newClientData={newClientData}
                                    setNewClientData={setNewClientData}
                                    clientMode={clientMode}
                                    setClientMode={setClientMode}
                                />
                            )}

                            {currentStep === 2 && (
                                <QuoteForm
                                    quoteData={quoteData}
                                    setQuoteData={setQuoteData}
                                    services={services}
                                    leadSources={leadSources}
                                />
                            )}

                            {currentStep === 3 && (
                                <JobForm
                                    jobData={jobData}
                                    setJobData={setJobData}
                                    surveyors={surveyors}
                                />
                            )}
                        </div>

                        <div className="px-6 md:px-8 pb-6">
                            <NavigationButtons
                                currentStep={currentStep}
                                onPrevious={handlePrevious}
                                onNext={handleNext}
                                onSubmit={handleSubmit}
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
